/**
 * FilePreviewModal — inline preview for a file living in Nova OS document
 * storage. Downloads the bytes with the caller's own bearer (same
 * `/api/documents/download/<path>` the "查看" button already used), then
 * renders by type:
 *   - pdf / image / text  → rendered natively in-browser (no backend)
 *   - Office (pptx/docx…) → POSTed to school-backend `/api/school/documents/
 *                           preview`, which returns a PDF we show in an iframe
 *   - anything else       → fall back to a plain download link
 *
 * Shared by RightRailArtifacts (产物) and FolderPanel (/documents) so both
 * "查看" surfaces get identical behavior.
 */
import { useEffect, useRef, useState } from "react";

import { openHtmlForPdf } from "../../lib/printHtml";
import { useTranslation } from "react-i18next";

import { authHeaders } from "../../api/school";
import { MHTML_EXTS, parseMhtml } from "../../lib/mhtml";

type Kind = "pdf" | "image" | "text" | "office" | "html" | "mhtml" | "other";

const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"];
const TEXT_EXTS = ["txt", "md", "markdown", "csv", "json", "log", "text", "yaml", "yml"];
const OFFICE_EXTS = ["pptx", "ppt", "docx", "doc", "xlsx", "xls", "odt", "odp", "ods"];

function kindOf(filename: string): Kind {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "pdf";
  if (MHTML_EXTS.includes(ext)) return "mhtml";
  if (ext === "html" || ext === "htm") return "html";
  if (IMAGE_EXTS.includes(ext)) return "image";
  if (TEXT_EXTS.includes(ext)) return "text";
  if (OFFICE_EXTS.includes(ext)) return "office";
  return "other";
}

interface PreviewState {
  status: "loading" | "ready" | "error" | "unsupported";
  kind: Kind;
  url?: string; // object URL for pdf/image/office
  text?: string; // decoded text for text kind
  srcDoc?: string; // parsed standalone HTML for html/mhtml (sandboxed iframe)
  error?: string;
}

// `downloadUrl` overrides the direct Nova OS path. Subject and group-library
// files MUST use it: Nova OS rewrites a non-admin caller's document path to
// users/<id>/ (authz/scope.go RewritePath), so a principal previewing
// subjects/english/… asks for a file that cannot exist and gets a 404 —
// which is exactly what 学科管理 showed for every file (Jarod, 2026-09-02).
// The school-backend proxy reads with the service account instead.
async function downloadBytes(path: string, downloadUrl?: string): Promise<Blob> {
  const resp = await fetch(
    downloadUrl ?? `/api/documents/download/${encodeURIComponent(path)}`,
    { headers: authHeaders() },
  );
  if (!resp.ok) throw new Error(`download ${resp.status}`);
  return resp.blob();
}

async function officeToPdf(blob: Blob, filename: string): Promise<Blob> {
  const fd = new FormData();
  fd.append("file", new File([blob], filename, { type: blob.type }));
  const resp = await fetch("/api/school/documents/preview", {
    method: "POST",
    headers: authHeaders(), // FormData sets its own content-type
    body: fd,
  });
  if (!resp.ok) {
    const body = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `preview ${resp.status}`);
  }
  return resp.blob();
}

export function FilePreviewModal({
  path,
  filename,
  onClose,
  downloadUrl,
}: {
  path: string;
  filename: string;
  onClose: () => void;
  downloadUrl?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<PreviewState>({
    status: "loading",
    kind: kindOf(filename),
  });
  const [frameMode, setFrameMode] = useState<"fit" | "raw">("fit");
  // ⌘P / Ctrl+P 在弹窗里:沙箱 iframe 打印出来是空白(博颂 2026-09-22),
  // 拦下来走「导出 PDF」的可打印页;PDF 直接新标签页开给浏览器阅读器打印。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "p") return;
      if (state.status !== "ready") return;
      e.preventDefault();
      if ((state.kind === "html" || state.kind === "mhtml") && state.srcDoc) {
        openHtmlForPdf(state.srcDoc, { print: String(t("documents.preview.printPdf")), hint: String(t("documents.preview.printPdfHint")), title: filename.replace(/\.(m?html?)$/i, "") });
      } else if (state.kind === "pdf" && state.url) {
        window.open(state.url, "_blank");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, filename, t]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | undefined;
    const kind = kindOf(filename);

    (async () => {
      try {
        if (kind === "other") {
          if (!cancelled) setState({ status: "unsupported", kind });
          return;
        }
        const blob = await downloadBytes(path, downloadUrl);
        if (cancelled) return;

        if (kind === "text") {
          const text = await blob.text();
          if (!cancelled) setState({ status: "ready", kind, text });
          return;
        }
        if (kind === "mhtml") {
          // 浏览器不渲染 HTTP 下发的 mhtml —— 前端解 MIME 后沙箱渲染。
          const { html } = parseMhtml(await blob.arrayBuffer());
          if (!cancelled) setState({ status: "ready", kind, srcDoc: html });
          return;
        }
        if (kind === "html") {
          const html = await blob.text();
          if (!cancelled) setState({ status: "ready", kind, srcDoc: html });
          return;
        }
        const rendered = kind === "office" ? await officeToPdf(blob, filename) : blob;
        if (cancelled) return;
        objectUrl = URL.createObjectURL(rendered);
        setState({
          status: "ready",
          // an office file is now a pdf; render it through the pdf branch
          kind: kind === "office" ? "pdf" : kind,
          url: objectUrl,
        });
      } catch (e) {
        if (!cancelled) {
          setState({ status: "error", kind, error: (e as Error).message });
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, filename]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-3">
          <span className="flex-1 truncate text-sm font-medium text-slate-800" title={filename}>
            {filename}
          </span>
          {state.status === "ready" && (state.kind === "mhtml" || state.kind === "html") && (
            <>
              <button
                type="button"
                className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                onClick={() => setFrameMode((m) => (m === "fit" ? "raw" : "fit"))}
              >
                {frameMode === "fit" ? t("documents.preview.rawSize") : t("documents.preview.fitWidth")}
              </button>
              {/* 导出 PDF(2026-09-21 用户):新标签页打开 + 自动弹打印对话框,
                  老师选「另存为 PDF」。见 lib/printHtml.ts 的安全说明。 */}
              <button
                type="button"
                className="rounded bg-sky-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-sky-600"
                data-testid="preview-export-pdf"
                onClick={() =>
                  openHtmlForPdf(state.srcDoc ?? "", {
                    print: String(t("documents.preview.printPdf")),
                    hint: String(t("documents.preview.printPdfHint")),
                    title: filename.replace(/\.(m?html?)$/i, ""),
                  })
                }
              >
                {t("documents.preview.exportPdf")}
              </button>
            </>
          )}
          {state.status === "ready" && state.kind === "pdf" && state.url && (
            <button type="button" className="rounded bg-sky-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-sky-600" onClick={() => window.open(state.url, "_blank")} data-testid="preview-open-pdf">
              {t("documents.preview.openToPrint")}
            </button>
          )}
          <button
            type="button"
            className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
            onClick={() => {
              // The download endpoint needs the bearer, so fetch the bytes
              // ourselves and save via a throwaway object URL.
              void downloadBytes(path, downloadUrl).then((b) => {
                const u = URL.createObjectURL(b);
                const a = document.createElement("a");
                a.href = u;
                a.download = filename;
                document.body.append(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(u), 1000);
              });
            }}
          >
            {t("documents.preview.download")}
          </button>
          <button
            onClick={onClose}
            className="rounded px-2 py-1 text-lg leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label={String(t("documents.preview.close"))}
          >
            ×
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-slate-50">
          {state.status === "loading" && (
            <div className="flex h-full items-center justify-center text-sm text-slate-500">
              {t("documents.preview.loading")}
            </div>
          )}
          {state.status === "error" && (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-rose-600">
              {t("documents.preview.error", { msg: state.error ?? "" })}
            </div>
          )}
          {state.status === "unsupported" && (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-slate-500">
              {t("documents.preview.unsupported")}
            </div>
          )}
          {state.status === "ready" && state.kind === "pdf" && (
            <iframe title={filename} src={state.url} className="h-full w-full border-0" />
          )}
          {state.status === "ready" && state.kind === "image" && (
            <div className="flex h-full items-center justify-center p-4">
              <img src={state.url} alt={filename} className="max-h-full max-w-full object-contain" />
            </div>
          )}
          {state.status === "ready" && (state.kind === "mhtml" || state.kind === "html") && (
            // allow-scripts 但不给 allow-same-origin:脚本在不透明源里
            // 跑(JS 渲染型报告能显示),但摸不到父页/凭据/存储,弹窗与
            // 顶层导航仍全禁 —— 外来 mhtml/html 的标准安全预览姿势。
            <ScaledFrame srcDoc={state.srcDoc ?? ""} title={filename} mode={frameMode} />
          )}
          {state.status === "ready" && state.kind === "text" && (
            <pre className="h-full w-full overflow-auto whitespace-pre-wrap break-words p-4 text-xs leading-relaxed text-slate-800">
              {state.text}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}


/** 沙箱 iframe 无法从外部量内容宽(opaque origin),所以提供两档:
 *  fit = 按 1920 设计宽渲染再缩放到容器宽(幻灯片/固定宽报告不截断);
 *  raw = 100% 宽原始渲染(响应式文档最自然)。默认 fit。 */
const DESIGN_W = 1920;

function ScaledFrame({ srcDoc, title, mode }: { srcDoc: string; title: string; mode: "fit" | "raw" }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    if (mode !== "fit") return;
    const el = boxRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / DESIGN_W));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mode]);

  if (mode === "raw") {
    return <iframe title={title} sandbox="allow-scripts" srcDoc={srcDoc} className="h-full w-full border-0 bg-white" />;
  }
  return (
    <div ref={boxRef} className="h-full w-full overflow-hidden">
      <iframe
        title={title}
        sandbox="allow-scripts"
        srcDoc={srcDoc}
        className="border-0 bg-white"
        style={{
          width: DESIGN_W,
          height: `${100 / scale}%`,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      />
    </div>
  );
}
